// State machine behind the composer's link-preview ghost.
//
// Watches the draft text for a URL and, when there is one and no other file is
// attached, offers to generate a sender-baked preview card. Generation is
// explicit (the user taps "Add"); once attached, the card can be removed or
// toggled between compact and hero without re-fetching.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { T } from '@deltachat/jsonrpc-client'

import { getLogger } from '@deltachat-desktop/shared/logger'
import { runtime } from '@deltachat-desktop/runtime-interface'

import { detectPreviewableUrl } from './detectUrl'
import {
  preparePreview,
  stageCard,
  UnfurlUnavailableError,
  type PreparedPreview,
  type StagedCard,
} from './generate'
import type { PreviewLayout } from './openGraph'

const log = getLogger('renderer/composer/linkPreview')

// Give up (and hide the spinner) if generation hasn't finished in this long —
// the core's own fetch caps at 60s and the unfurl fetch has no timeout, so a
// hung request would otherwise leave the loading state forever.
const GENERATION_TIMEOUT_MS = 25_000

export type LinkPreviewPhase =
  | { kind: 'idle'; host: string } // ghost offering to add a preview
  // Carries the host so the loading card keeps showing it — idle → loading is
  // the same skeleton card, just shimmering.
  | { kind: 'loading'; host: string }
  | {
      kind: 'ready'
      host: string
      title: string
      layout: PreviewLayout
      /** `data:` URL of the baked card, shown as the preview in the chip. */
      image: string
    }
  // The unfurl endpoint couldn't be reached: show a quiet "needs a bridge with
  // unfurl enabled" hint instead of silently doing nothing.
  | { kind: 'unavailable'; host: string }

export type UseLinkPreview = {
  /** Non-null when the ghost/card should be shown in the composer. */
  phase: LinkPreviewPhase | null
  /** True while a generated preview is the current draft attachment. */
  isPreviewActive: boolean
  onAdd: () => void
  onRemove: () => void
  onToggleLayout: () => void
  onDismiss: () => void
}

type Params = {
  accountId: number
  chatId: number
  text: string
  /** The current draft's attached file path, if any. */
  draftFile: string | null
  addFileToDraft: (
    file: string,
    fileName: string | null,
    viewType: T.Viewtype
  ) => Promise<void>
  removeFile: () => void
  /** Disable the feature entirely (e.g. while editing an existing message). */
  disabled?: boolean
}

const DETECT_DEBOUNCE_MS = 400

export function useLinkPreview(params: Params): UseLinkPreview {
  const { accountId, chatId, text, draftFile, addFileToDraft, removeFile } =
    params

  // Debounced URL detection so the ghost doesn't flicker while typing.
  const [detected, setDetected] = useState(() => detectPreviewableUrl(text))
  useEffect(() => {
    const id = setTimeout(
      () => setDetected(detectPreviewableUrl(text)),
      DETECT_DEBOUNCE_MS
    )
    return () => clearTimeout(id)
  }, [text])

  const url = detected?.url ?? null
  const host = detected?.host ?? ''

  const [phase, setPhase] = useState<LinkPreviewPhase | null>(null)
  const [isPreviewActive, setIsPreviewActive] = useState(false)
  // Mirror of `isPreviewActive` that updates synchronously: our own
  // `addFileToDraft` changes `draftFile` in the same tick we mark the preview
  // active, and `userFilePresent` (read during render) must already see the
  // flag flipped — otherwise the URL effect briefly treats our fresh preview
  // as a user file and drops it.
  const previewActiveRef = useRef(false)
  const setPreviewActive = useCallback((active: boolean) => {
    previewActiveRef.current = active
    setIsPreviewActive(active)
  }, [])

  // The last URL the user dismissed (or that a preview was removed/failed for);
  // the ghost stays hidden while that exact URL is in the composer. Reset once
  // the composer has no URL (message sent, link deleted) so the same link in a
  // new message offers the ghost again — the dismissal is per-message, not a
  // permanent per-chat mute.
  const dismissedRef = useRef<string | null>(null)
  // Increments on every URL change / action to invalidate in-flight async work.
  const tokenRef = useRef(0)
  // Kept in memory so layout toggles re-render without re-fetching.
  const preparedRef = useRef<PreparedPreview | null>(null)
  const stagedPathRef = useRef<string | null>(null)

  const clearStagedTemp = useCallback(() => {
    if (stagedPathRef.current) {
      runtime.removeTempFile(stagedPathRef.current).catch(() => {})
      stagedPathRef.current = null
    }
  }, [])

  // A file the user attached themselves — i.e. a draft file that is NOT our
  // generated preview. Our own preview keeps `isPreviewActive` in lockstep with
  // `draftFile`, so attaching/removing it never flips this; it changes only when
  // the user attaches or removes their own file.
  // `draftFile` is '' (not null) when empty, so test truthiness. Uses the ref,
  // not the state, so our own attach (which flips the ref synchronously before
  // changing `draftFile`) never reads as a user file.
  const userFilePresent = !!draftFile && !previewActiveRef.current

  // React to the detected URL changing (or disappearing), or to the user
  // attaching/removing their own file. Keying on `userFilePresent` (not raw
  // `draftFile`) is what lets this react to the user's files without our own
  // attach re-triggering it and dropping the fresh preview.
  useEffect(() => {
    // Any change here invalidates in-flight generation and the memoized preview.
    tokenRef.current++
    clearStagedTemp()
    preparedRef.current = null

    // If our preview is attached when this runs, the reason is always a stale
    // one (the URL changed or was removed — `userFilePresent` can't be true
    // while a preview is active), so drop it.
    if (previewActiveRef.current) {
      removeFile()
      setPreviewActive(false)
    }

    // No URL in the composer (sent, cleared, or link removed): forget any
    // dismissal so the same link, typed again later, offers the ghost afresh.
    if (!url) {
      dismissedRef.current = null
    }

    if (
      params.disabled ||
      !url ||
      userFilePresent ||
      dismissedRef.current === url
    ) {
      setPhase(null)
      return
    }
    setPhase({ kind: 'idle', host })
    // `host` tracks `url`; `isPreviewActive` is read but deliberately not a dep
    // (our own attach must not retrigger this effect and remove the preview).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, params.disabled, userFilePresent])

  const attach = useCallback(
    async (
      prepared: PreparedPreview,
      layout: PreviewLayout,
      token: number
    ): Promise<StagedCard | null> => {
      const staged = await stageCard(prepared, layout)
      if (token !== tokenRef.current) {
        runtime.removeTempFile(staged.path).catch(() => {})
        return null
      }
      stagedPathRef.current = staged.path
      // Mark ours BEFORE addFileToDraft changes draftFile, so the URL effect
      // doesn't mistake the fresh attachment for a user file (see the ref).
      setPreviewActive(true)
      // Attach as 'File', not 'Image': core promotes an image File to Image on
      // send *without* recompressing it (chat.rs check_or_recode_image early-
      // returns for File). Sending as 'Image' recodes to JPEG once the PNG tops
      // ~500 KB (or 130 KB on "worse" media quality), and JPEG has no alpha, so
      // into_rgb8() turns the card's transparent rounded-corner/shadow margin
      // black. 'File' keeps the PNG (with transparency) byte-for-byte.
      await addFileToDraft(staged.path, staged.fileName, 'File')
      // The core copied the file into its blob dir; the temp copy is disposable.
      clearStagedTemp()
      return staged
    },
    [addFileToDraft, clearStagedTemp, setPreviewActive]
  )

  const onAdd = useCallback(() => {
    // Nothing to preview, or the user has their own file attached — never
    // clobber that. (The ghost is already hidden in that case; guard anyway.)
    if (!url || (!!draftFile && !isPreviewActive)) {
      return
    }
    // slothfulchat: anonymous analytics — the user accepted a link preview
    ;(window as any).__slothfulTrack?.('link_preview', { action: 'accept' })
    const token = ++tokenRef.current
    setPhase({ kind: 'loading', host })
    // Bail out of a hung fetch so the shimmer doesn't linger forever.
    const timeout = setTimeout(() => {
      if (token === tokenRef.current) {
        tokenRef.current++ // cancel the in-flight continuation
        setPhase(null)
        dismissedRef.current = url
        log.info('link preview generation timed out', url)
      }
    }, GENERATION_TIMEOUT_MS)
    ;(async () => {
      const prepared = await preparePreview(accountId, url)
      if (token !== tokenRef.current) {
        return
      }
      if (!prepared) {
        // Nothing worth showing — quietly give up (no manual editor by design).
        setPhase(null)
        dismissedRef.current = url
        return
      }
      preparedRef.current = prepared
      const staged = await attach(prepared, prepared.initialLayout, token)
      if (token !== tokenRef.current) {
        return
      }
      if (!staged) {
        setPhase(null)
        return
      }
      // `attach` already flipped isPreviewActive on success.
      setPhase({
        kind: 'ready',
        host: prepared.meta.host,
        title: prepared.meta.title,
        layout: prepared.initialLayout,
        image: staged.dataUrl,
      })
    })()
      .catch(err => {
        log.warn('link preview generation failed', err)
        if (token === tokenRef.current) {
          // Endpoint unreachable → hint the user; any other failure (no preview
          // for this page) just quietly clears.
          setPhase(
            err instanceof UnfurlUnavailableError
              ? { kind: 'unavailable', host }
              : null
          )
          dismissedRef.current = url
        }
      })
      .finally(() => clearTimeout(timeout))
  }, [accountId, url, host, draftFile, isPreviewActive, attach])

  const onRemove = useCallback(() => {
    // slothfulchat: the user dismissed (removed) an attached preview
    ;(window as any).__slothfulTrack?.('link_preview', { action: 'dismiss' })
    tokenRef.current++
    clearStagedTemp()
    if (previewActiveRef.current) {
      removeFile()
      setPreviewActive(false)
    }
    if (url) {
      dismissedRef.current = url
    }
    setPhase(null)
  }, [clearStagedTemp, removeFile, setPreviewActive, url])

  const onDismiss = useCallback(() => {
    // slothfulchat: the user dismissed the link-preview ghost
    ;(window as any).__slothfulTrack?.('link_preview', { action: 'dismiss' })
    if (url) {
      dismissedRef.current = url
    }
    setPhase(null)
  }, [url])

  const onToggleLayout = useCallback(() => {
    const prepared = preparedRef.current
    if (!prepared || phase?.kind !== 'ready') {
      return
    }
    const next: PreviewLayout = phase.layout === 'hero' ? 'compact' : 'hero'
    const prevImage = phase.image
    const token = ++tokenRef.current
    setPhase({ kind: 'loading', host: prepared.meta.host })
    ;(async () => {
      const staged = await attach(prepared, next, token)
      if (token !== tokenRef.current) {
        return
      }
      setPhase(
        staged
          ? {
              kind: 'ready',
              host: prepared.meta.host,
              title: prepared.meta.title,
              layout: next,
              image: staged.dataUrl,
            }
          : null
      )
      // `attach` already flipped isPreviewActive on success.
    })().catch(err => {
      log.warn('link preview re-render failed', err)
      if (token === tokenRef.current) {
        setPhase({
          kind: 'ready',
          host: prepared.meta.host,
          title: prepared.meta.title,
          layout: phase.layout,
          image: prevImage,
        })
      }
    })
  }, [attach, phase])

  // Clean up any staged temp file on unmount / chat switch.
  useEffect(() => {
    return () => {
      clearStagedTemp()
    }
  }, [chatId, clearStagedTemp])

  return useMemo(
    () => ({ phase, isPreviewActive, onAdd, onRemove, onToggleLayout, onDismiss }),
    [phase, isPreviewActive, onAdd, onRemove, onToggleLayout, onDismiss]
  )
}
